/**
 * @file Drop entry for the Form page. For an intake reviewer (`aiOperator` or
 * `aiAdmin`) a dropped or browsed PDF goes straight to PDF intake
 * (`startPdfIntake`) bound to the form version open in `formStore`, and the
 * page opens its schedule review at `/intake/schedules/<sha>`. Every other
 * file, and a PDF from anyone else, is imported into that version by the agent
 * (`AIContext.importIntoForm`). No new form is created either way. Mirrors
 * `useFileDrop` (Home / Forms-list). Disabled until the `aiAgent` right is held
 * and a form version is selected.
 */
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { AGENT_EMAIL_EXTENSIONS, AGENT_TABULAR_EXTENSIONS } from '#types';
import { UserContext } from '../providers/UserContext';
import { AIContext } from '../providers/AIContext';
import { MessageContext } from '../providers/MessageContext';
import { startPdfIntake } from '../services/startPdfIntake';
import { formStore } from '../store';

const VALID_DROP_EXTENSIONS = [
  ...AGENT_EMAIL_EXTENSIONS,
  ...AGENT_TABULAR_EXTENSIONS,
].map((ext) => `.${ext}`);

const useFormImportDrop = () => {
  const { user, userRights } = useContext(UserContext);
  const { importIntoForm } = useContext(AIContext);
  const { messageApi } = useContext(MessageContext);
  const navigate = useNavigate();
  const snap = useSnapshot(formStore.state);

  const formId = snap.formId;
  const formVersionId = snap.versionId;
  const dropDisabled = !userRights.aiAgent || !formId || !formVersionId;
  const pdfsOpenInIntake = !!(userRights.aiOperator || userRights.aiAdmin);

  const handleDrop = async (file: File) => {
    if (!formId || !formVersionId) return;
    if (pdfsOpenInIntake && user && file.name.toLowerCase().endsWith('.pdf')) {
      const jobId = await startPdfIntake({
        file,
        order: { formId, formVersionId },
        userId: user.uid,
        messageApi,
      });
      if (jobId) navigate(`/intake/schedules/${jobId}`);
      return;
    }
    await importIntoForm([file], { formId, formVersionId });
  };

  return {
    dropDisabled,
    pdfsOpenInIntake,
    validDropExtensions: VALID_DROP_EXTENSIONS,
    handleDrop,
    handleFileBrowse: handleDrop,
  };
};

export default useFormImportDrop;
